import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/lib/auth-context";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  FileText,
  User,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Pen,
  FileSignature,
  Loader2,
  ExternalLink,
  RefreshCw,
  Upload,
  Send,
  Eye,
  Save,
  File,
  Trash2,
  Shield,
  MessageSquare,
  FolderOpen,
  X,
  Check,
  Info,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  MapPin,
  Hash,
  Building,
  Globe,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

// Status styling map
const STATUS_MAP = {
  DRAFT: { label: "Brouillon", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300", icon: Clock },
  TO_COMPLETE: { label: "À compléter", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", icon: AlertCircle },
  CONTRACT_DRAFT: { label: "Contrat brouillon", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300", icon: FileText },
  SUBMITTED: { label: "Soumis", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", icon: Clock },
  UNDER_REVIEW: { label: "En revue", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300", icon: Eye },
  APPROVED: { label: "Approuvé", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: CheckCircle },
  PENDING_REVIEW: { label: "En révision", color: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300", icon: Clock },
  CHANGE_REQUESTED: { label: "Modification demandée", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", icon: Pen },
  VALIDATED: { label: "Validé", color: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300", icon: CheckCircle },
  CONTRACT_VALIDATED: { label: "Contrat validé", color: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300", icon: CheckCircle },
  SENT_FOR_SIGNATURE: { label: "En signature", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", icon: FileSignature },
  SIGNED: { label: "Signé", color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", icon: CheckCircle },
  COMPLETED: { label: "Terminé", color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", icon: CheckCircle },
};

// Field icons mapping
const FIELD_ICONS = {
  first_name: User,
  last_name: User,
  birth_date: Calendar,
  birth_place: MapPin,
  phone: Phone,
  email: Mail,
  address: MapPin,
  city: Building,
  postal_code: Hash,
  nationality: Globe,
  social_security: Shield,
  employer_name: Building,
};

// Field labels in French (fallback)
const FIELD_LABELS = {
  first_name: "Prénom",
  last_name: "Nom",
  birth_date: "Date de naissance",
  birth_place: "Lieu de naissance",
  phone: "Téléphone",
  email: "Email",
  address: "Adresse",
  city: "Ville",
  postal_code: "Code postal",
  nationality: "Nationalité",
  social_security: "N° Sécurité sociale",
  employer_name: "Employeur",
  program_name: "Formation",
  session_name: "Session",
  start_date: "Date de début",
  end_date: "Date de fin",
  total_price: "Prix total",
  funding_source: "Financement",
};

export default function MonDossierPage() {
  const { user } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Contract viewer state
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const contractViewerRef = useRef(null);

  // Field editing state
  const [editedFields, setEditedFields] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [validatingContract, setValidatingContract] = useState(false);

  // Documents state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [docStatuses, setDocStatuses] = useState([]);
  const fileInputRef = useRef(null);
  const docTypeFileRef = useRef(null);

  // Contact RH state
  const [rhMessage, setRhMessage] = useState("");
  const [rhSubject, setRhSubject] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const loadDossier = async () => {
    if (!user?.email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/efiflow-api/integration/dossier/${encodeURIComponent(user.email)}`);
      if (!res.ok) {
        if (res.status === 404) {
          setDossier(null);
          return;
        }
        throw new Error("Erreur de chargement du dossier");
      }
      const data = await res.json();
      setDossier(data);
      setEditedFields({});
    } catch (err) {
      console.error("Failed to load dossier:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDossier();
  }, [user?.email]);

  // === Contract paginated content ===
  const contract = dossier?.contracts?.[0] || null;

  const contractPages = useMemo(() => {
    if (!contract?.renderedHtml) return [];
    const html = contract.renderedHtml;

    // Split by explicit page breaks first
    const byPageBreak = html.split(/<!--\s*page-break\s*-->|<div[^>]*class="page-break"[^>]*>.*?<\/div>|<hr[^>]*class="page-break"[^>]*\/?>/gi);
    if (byPageBreak.length > 1) return byPageBreak.filter(p => p.trim());

    // If no explicit page breaks, split by major sections (h1, h2 blocks)
    const sections = html.split(/(?=<h[12][^>]*>)/gi);
    if (sections.length > 2) {
      // Group into pages of ~2-3 sections
      const pages = [];
      let current = "";
      for (let i = 0; i < sections.length; i++) {
        current += sections[i];
        // Create page every ~2 sections or if content is long enough
        if ((i > 0 && (i % 2 === 1 || current.length > 3000)) || i === sections.length - 1) {
          if (current.trim()) pages.push(current);
          current = "";
        }
      }
      if (current.trim()) pages.push(current);
      return pages.length > 0 ? pages : [html];
    }

    // Fallback: single page
    return [html];
  }, [contract?.renderedHtml]);

  const totalPages = contractPages.length || 1;

  // === Field change tracking ===
  const hasChanges = Object.keys(editedFields).length > 0;

  const handleFieldChange = (key, value) => {
    const originalValue = contract?.variables?.[key] || "";
    if (value === originalValue) {
      const next = { ...editedFields };
      delete next[key];
      setEditedFields(next);
    } else {
      setEditedFields({ ...editedFields, [key]: value });
    }
  };

  // === Validate contract (with optional modifications applied immediately) ===
  const handleValidateContract = async () => {
    if (!contract) return;
    setValidatingContract(true);
    try {
      const res = await fetch(`/efiflow-api/integration/contract-validate/${contract.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user?.email,
          fields: hasChanges ? editedFields : undefined,
        }),
      });
      if (!res.ok) throw new Error("Erreur lors de la validation");
      const result = await res.json();
      toast.success(hasChanges
        ? "Modifications appliquées et contrat validé ! Rendez-vous dans l'onglet Signature."
        : "Contrat validé avec succès ! Rendez-vous dans l'onglet Signature."
      );
      setEditedFields({});
      setIsEditing(false);
      await loadDossier();
      // Redirect to signature tab
      setActiveTab("signature");
    } catch {
      toast.error("Erreur lors de la validation du contrat.");
    } finally {
      setValidatingContract(false);
    }
  };

  // === Documents Tab Handlers ===
  // Poll document statuses every 30s
  useEffect(() => {
    if (!user?.email) return;
    const fetchStatuses = async () => {
      try {
        const res = await fetch(`/efiflow-api/integration/document-statuses/${encodeURIComponent(user.email)}`);
        if (res.ok) {
          const data = await res.json();
          setDocStatuses(data.statuses || []);
        }
      } catch (err) {
        console.error("Failed to poll document statuses:", err);
      }
    };
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 30000);
    return () => clearInterval(interval);
  }, [user?.email]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingDoc(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("candidateId", dossier.candidate.id);
        await fetch("/efiflow-api/integration/documents/upload/" + dossier.candidate.id, {
          method: "POST",
          body: formData,
        });
      }
      toast.success(`${files.length} document(s) envoyé(s) !`);
      loadDossier();
    } catch {
      toast.error("Erreur lors de l'envoi du document.");
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDocTypeUpload = async (e, docTypeId) => {
    const files = e.target.files;
    if (!files?.length || !dossier?.candidate?.id) return;
    setUploadingDocType(docTypeId);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("documentTypeId", docTypeId);
        await fetch(`/efiflow-api/integration/documents/upload/${dossier.candidate.id}`, {
          method: "POST",
          body: formData,
        });
      }
      toast.success("Document envoyé !");
      loadDossier();
      // Refresh statuses immediately
      const res = await fetch(`/efiflow-api/integration/document-statuses/${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const data = await res.json();
        setDocStatuses(data.statuses || []);
      }
    } catch {
      toast.error("Erreur lors de l'envoi du document.");
    } finally {
      setUploadingDocType(null);
      if (docTypeFileRef.current) docTypeFileRef.current.value = "";
    }
  };

  const handleResetDocuments = async () => {
    if (!dossier?.candidate?.id) return;
    if (!window.confirm("Voulez-vous vraiment supprimer tous vos documents uploadés ? Vous pourrez les renvoyer ensuite.")) return;
    try {
      const res = await fetch(`/efiflow-api/integration/documents/reset/${dossier.candidate.id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Documents réinitialisés");
        setDocStatuses([]);
        loadDossier();
      } else {
        toast.error("Erreur lors de la réinitialisation");
      }
    } catch {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  // Document status badge helper
  const getDocStatusBadge = (status) => {
    const map = {
      VALIDATED: { label: "Validé", icon: "✅", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" },
      REJECTED: { label: "À refaire", icon: "🔄", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800" },
      PENDING: { label: "En attente", icon: "⏳", className: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400 border-slate-200 dark:border-slate-700" },
      NOT_PROVIDED: { label: "Non fourni", icon: "➕", className: "bg-gray-50 text-gray-500 dark:bg-gray-900/40 dark:text-gray-500 border-gray-200 dark:border-gray-800" },
    };
    const cfg = map[status] || map.NOT_PROVIDED;
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border ${cfg.className}`}>
        <span>{cfg.icon}</span>
        {cfg.label}
      </span>
    );
  };

  // === Contact RH Handler ===
  const handleSendMessage = async () => {
    if (!rhMessage.trim() || !rhSubject.trim()) {
      toast.error("Veuillez remplir le sujet et le message.");
      return;
    }
    setSendingMessage(true);
    try {
      await fetch("/efiflow-api/integration/contact-rh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidateId: dossier?.candidate?.id,
          email: user?.email,
          subject: rhSubject,
          message: rhMessage,
        }),
      });
      toast.success("Message envoyé aux RH !");
      setRhMessage("");
      setRhSubject("");
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSendingMessage(false);
    }
  };

  // === Signature Handler — fetches a fresh signing URL each time ===
  const [loadingSignUrl, setLoadingSignUrl] = useState(false);
  const handleSign = async () => {
    if (!contract) return;
    setLoadingSignUrl(true);
    try {
      // Try to get a fresh signing URL from the API
      const res = await fetch(`/efiflow-api/integration/signing-url/${contract.id}`);
      if (res.ok) {
        const data = await res.json();
        // If already signed, refresh dossier to show signed state
        if (data.status === "SIGNED") {
          toast.success("Votre contrat a été signé avec succès !");
          await loadDossier();
          setLoadingSignUrl(false);
          return;
        }
        if (data.signatureUrl) {
          window.open(data.signatureUrl, "_blank");
          setLoadingSignUrl(false);
          return;
        }
      }
      // Fallback to stored signatureUrl
      if (contract.signatureUrl) {
        window.open(contract.signatureUrl, "_blank");
      } else {
        toast.info("Le lien de signature est en cours de génération. Réessayez dans quelques instants.");
      }
    } catch {
      // Fallback to stored URL
      if (contract.signatureUrl) {
        window.open(contract.signatureUrl, "_blank");
      } else {
        toast.info("Le lien de signature sera disponible prochainement.");
      }
    } finally {
      setLoadingSignUrl(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = STATUS_MAP[status] || { label: status, color: "bg-gray-100 text-gray-700", icon: Clock };
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${s.color}`}>
        <Icon className="h-3.5 w-3.5" />
        {s.label}
      </span>
    );
  };

  // Can the candidate still edit / validate?
  const canEditContract = contract && ["DRAFT", "CONTRACT_DRAFT", "PENDING_REVIEW", "CHANGE_REQUESTED"].includes(contract.status);
  const isContractValidated = contract && ["VALIDATED", "FINAL", "SENT_FOR_SIGNATURE", "SIGNED", "COMPLETED"].includes(contract.status);

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              Mon dossier
            </h1>
            <p className="mt-1 text-muted-foreground">
              Gérez votre candidature, contrat et documents
            </p>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={loadDossier}>
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="font-semibold text-lg">Erreur</h3>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              <Button variant="outline" className="mt-4" onClick={loadDossier}>Réessayer</Button>
            </CardContent>
          </Card>
        ) : !dossier ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-lg">Aucun dossier trouvé</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Votre dossier de candidature n'a pas encore été créé par l'administration.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Status Banner */}
            <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-purple-500/5">
              <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">
                      {dossier.candidate?.firstName} {dossier.candidate?.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">{dossier.candidate?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(dossier.candidate?.status)}
                  {dossier.session && (
                    <div className="text-right hidden sm:block">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Session</p>
                      <p className="text-sm font-semibold">{dossier.session.name}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 h-12">
                <TabsTrigger value="overview" className="gap-2 text-xs sm:text-sm">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Contrat</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className="gap-2 text-xs sm:text-sm">
                  <FolderOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Documents</span>
                </TabsTrigger>
                <TabsTrigger value="contact" className="gap-2 text-xs sm:text-sm">
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Contact RH</span>
                </TabsTrigger>
                <TabsTrigger value="signature" className="gap-2 text-xs sm:text-sm">
                  <FileSignature className="h-4 w-4" />
                  <span className="hidden sm:inline">Signature</span>
                </TabsTrigger>
              </TabsList>

              {/* ============== TAB 1: CONTRAT ============== */}
              <TabsContent value="overview" className="space-y-4">
                {contract ? (
                  <>
                    {/* Contract header */}
                    <Card>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <FileText className="h-5 w-5 text-primary" />
                            Contrat de formation
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(contract.status)}
                            <Badge variant="outline" className="text-xs">V{contract.currentVersion}</Badge>
                          </div>
                        </div>
                        <CardDescription>
                          Template : {contract.template?.name || "Standard"} • Créé le {new Date(contract.createdAt).toLocaleDateString("fr-FR")}
                        </CardDescription>
                      </CardHeader>
                    </Card>

                    {/* Two-column layout: Viewer + Fields panel */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                      {/* LEFT: Contract Viewer (60%) */}
                      <div className="lg:col-span-3">
                        <Card className="overflow-hidden">
                          <CardContent className="p-0">
                            {/* Viewer toolbar */}
                            <div className="flex items-center justify-between px-4 py-2.5 bg-muted/50 border-b">
                              <div className="flex items-center gap-2">
                                <Eye className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Aperçu du contrat</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setZoom(Math.max(50, zoom - 10))}
                                  disabled={zoom <= 50}
                                >
                                  <ZoomOut className="h-3.5 w-3.5" />
                                </Button>
                                <span className="text-xs text-muted-foreground w-10 text-center">{zoom}%</span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setZoom(Math.min(150, zoom + 10))}
                                  disabled={zoom >= 150}
                                >
                                  <ZoomIn className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>

                            {/* Document viewer */}
                            <div className="bg-slate-100 dark:bg-slate-900/50 p-4 sm:p-6 min-h-[600px] flex flex-col items-center" ref={contractViewerRef}>
                              {contract.renderedHtml ? (
                                <>
                                  {/* A4 Page */}
                                  <div
                                    className="bg-white dark:bg-gray-950 shadow-xl border border-gray-200 dark:border-gray-800 rounded-sm mx-auto transition-transform duration-200"
                                    style={{
                                      width: `${Math.min(210 * (zoom / 100), 100)}%`,
                                      maxWidth: `${210 * (zoom / 100) * 3.78}px`,
                                      minHeight: `${297 * (zoom / 100) * 3.78}px`,
                                      transform: `scale(1)`,
                                    }}
                                  >
                                    <div
                                      className="p-8 sm:p-12 prose prose-sm dark:prose-invert max-w-none
                                        [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-center [&_h1]:uppercase [&_h1]:tracking-wide
                                        [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:border-b [&_h2]:pb-2
                                        [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2
                                        [&_p]:my-2 [&_p]:leading-relaxed [&_p]:text-sm
                                        [&_strong]:font-semibold [&_strong]:text-foreground
                                        [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:p-2 [&_td]:text-sm
                                        [&_th]:border [&_th]:p-2 [&_th]:bg-gray-50 [&_th]:text-sm [&_th]:font-semibold
                                        [&_ul]:my-2 [&_ul]:pl-6 [&_li]:text-sm [&_li]:my-1"
                                      style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: `${zoom * 0.14}px` }}
                                      dangerouslySetInnerHTML={{ __html: contractPages[currentPage - 1] || contract.renderedHtml }}
                                    />
                                  </div>

                                  {/* Pagination */}
                                  {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-3 mt-4 py-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-1.5 h-8"
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage <= 1}
                                      >
                                        <ChevronLeft className="h-3.5 w-3.5" />
                                        Précédent
                                      </Button>
                                      <span className="text-sm font-medium text-muted-foreground px-2">
                                        Page {currentPage} / {totalPages}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-1.5 h-8"
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage >= totalPages}
                                      >
                                        Suivant
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-center">
                                  <FileText className="h-16 w-16 text-muted-foreground/30 mb-4" />
                                  <h3 className="font-semibold text-lg text-muted-foreground">Contrat en cours de génération</h3>
                                  <p className="text-sm text-muted-foreground/70 mt-1">
                                    Le contenu du contrat sera disponible prochainement.
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* RIGHT: Editable Fields Panel (40%) */}
                      <div className="lg:col-span-2">
                        <Card className="sticky top-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Pen className="h-4 w-4 text-primary" />
                              Vos informations
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {isEditing
                                ? "Modifiez vos informations puis validez votre contrat"
                                : isContractValidated
                                  ? "Contrat validé — les informations sont verrouillées"
                                  : canEditContract
                                    ? "Cliquez sur Modifier pour mettre à jour vos informations"
                                    : "Informations en lecture seule"
                              }
                            </CardDescription>
                          </CardHeader>
                          <Separator />
                          <CardContent className="pt-4 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
                            {contract.editableFields?.length > 0 ? (
                              <>
                                {contract.editableFields.map((field) => {
                                  const FieldIcon = FIELD_ICONS[field.key] || User;
                                  const currentValue = editedFields[field.key] !== undefined
                                    ? editedFields[field.key]
                                    : contract.variables?.[field.key] || "";
                                  const isModified = editedFields[field.key] !== undefined;
                                  const fieldLabel = field.label || FIELD_LABELS[field.key] || field.key;

                                  return (
                                    <div key={field.key} className="space-y-1.5">
                                      <Label
                                        htmlFor={`field-${field.key}`}
                                        className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground"
                                      >
                                        <FieldIcon className="h-3 w-3" />
                                        {fieldLabel}
                                        {isModified && (
                                          <span className="ml-auto text-[10px] text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide">
                                            Modifié
                                          </span>
                                        )}
                                      </Label>
                                      <Input
                                        id={`field-${field.key}`}
                                        type={field.type === "date" ? "date" : "text"}
                                        value={field.type === "date" && currentValue && !currentValue.includes("-")
                                          ? ""
                                          : currentValue
                                        }
                                        placeholder={
                                          field.type === "date" && currentValue && !currentValue.includes("-")
                                            ? currentValue
                                            : `Entrez votre ${fieldLabel.toLowerCase()}`
                                        }
                                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                        disabled={!isEditing}
                                        className={`h-9 text-sm transition-all ${
                                          isModified
                                            ? "border-amber-400 bg-amber-50/50 dark:bg-amber-900/10 ring-1 ring-amber-400/30"
                                            : !isEditing
                                              ? "bg-muted/40 text-muted-foreground"
                                              : ""
                                        } ${!isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                                      />
                                    </div>
                                  );
                                })}

                                <Separator className="my-4" />

                                {/* Action buttons */}
                                {canEditContract && !isEditing && (
                                  <div className="space-y-2.5">
                                    <Button
                                      className="w-full gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                                      onClick={() => setIsEditing(true)}
                                    >
                                      <Pen className="h-4 w-4" />
                                      Modifier mes informations
                                    </Button>
                                    <Button
                                      className="w-full gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                                      onClick={handleValidateContract}
                                      disabled={validatingContract}
                                    >
                                      {validatingContract ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4" />
                                      )}
                                      Valider le contrat
                                    </Button>
                                  </div>
                                )}

                                {canEditContract && isEditing && (
                                  <div className="space-y-2.5">
                                    <Button
                                      className="w-full gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg"
                                      onClick={handleValidateContract}
                                      disabled={validatingContract}
                                    >
                                      {validatingContract ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4" />
                                      )}
                                      {hasChanges ? "Appliquer et valider" : "Valider le contrat"}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full text-xs"
                                      onClick={() => { setEditedFields({}); setIsEditing(false); }}
                                    >
                                      <X className="h-3.5 w-3.5 mr-1" />
                                      Annuler
                                    </Button>
                                    {hasChanges && (
                                      <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                                        {Object.keys(editedFields).length} champ(s) modifié(s) — les modifications seront appliquées automatiquement
                                      </p>
                                    )}
                                  </div>
                                )}

                                {isContractValidated && (
                                  <div className="space-y-2.5">
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                      <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                                      <p className="text-xs text-emerald-700 dark:text-emerald-400">
                                        Contrat validé ! Rendez-vous dans l'onglet Signature pour signer.
                                      </p>
                                    </div>
                                    <Button
                                      className="w-full gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white"
                                      onClick={() => setActiveTab("signature")}
                                    >
                                      <FileSignature className="h-4 w-4" />
                                      Aller à la signature
                                    </Button>
                                  </div>
                                )}

                                {/* Info notice */}
                                {canEditContract && !isEditing && (
                                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 mt-3">
                                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                    <p className="text-[11px] text-blue-700 dark:text-blue-400 leading-relaxed">
                                      Cliquez sur « Modifier » pour mettre à jour vos informations. Si tout est correct, cliquez directement sur « Valider ».
                                    </p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-8 text-center">
                                <Shield className="h-10 w-10 text-muted-foreground/30 mb-3" />
                                <p className="text-sm text-muted-foreground">
                                  Aucun champ modifiable n'a été configuré par l'administration.
                                </p>
                                {canEditContract && (
                                  <Button
                                    className="mt-4 gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                                    onClick={handleValidateContract}
                                    disabled={validatingContract}
                                  >
                                    {validatingContract ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <CheckCircle className="h-4 w-4" />
                                    )}
                                    Valider le contrat
                                  </Button>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </>
                ) : (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="font-semibold text-lg">Aucun contrat</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Votre contrat sera généré par l'administration une fois votre dossier validé.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* ============== TAB 2: DOCUMENTS ============== */}
              <TabsContent value="documents" className="space-y-4">
                {/* Upload zone */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Upload className="h-5 w-5 text-primary" />
                      Envoi de documents
                    </CardTitle>
                    <CardDescription>
                      Envoyez les documents requis pour votre dossier de candidature
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div
                      className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      {uploadingDoc ? (
                        <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary mb-3" />
                      ) : (
                        <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      )}
                      <p className="font-medium">Cliquez pour envoyer vos documents</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        PDF, JPG, PNG, DOC — Max 10 Mo par fichier
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Documents by type with status badges */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <FolderOpen className="h-5 w-5 text-primary" />
                          Statut de vos documents
                        </CardTitle>
                        <CardDescription>
                          Suivez l'état de validation de chaque document
                        </CardDescription>
                      </div>
                      {docStatuses.some(ds => ds.originalName || ds.status === "PENDING") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResetDocuments}
                          className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-1"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Réinitialiser
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {docStatuses.length > 0 ? (
                      <div className="space-y-3">
                        {docStatuses.map((ds) => (
                          <div
                            key={ds.documentTypeId}
                            className={`flex items-center justify-between rounded-xl border p-4 transition-all duration-200 ${
                              ds.status === "VALIDATED"
                                ? "bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50"
                                : ds.status === "REJECTED"
                                  ? "bg-orange-50/50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/50"
                                  : ds.status === "PENDING"
                                    ? "bg-muted/30 border-border"
                                    : "bg-muted/10 border-border/50"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                                ds.status === "VALIDATED" ? "bg-emerald-100 dark:bg-emerald-900/30" :
                                ds.status === "REJECTED" ? "bg-orange-100 dark:bg-orange-900/30" :
                                ds.status === "PENDING" ? "bg-primary/10" :
                                "bg-muted"
                              }`}>
                                <File className={`h-5 w-5 ${
                                  ds.status === "VALIDATED" ? "text-emerald-600 dark:text-emerald-400" :
                                  ds.status === "REJECTED" ? "text-orange-600 dark:text-orange-400" :
                                  ds.status === "PENDING" ? "text-primary" :
                                  "text-muted-foreground"
                                }`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">{ds.documentTypeName}</p>
                                  {ds.isRequired && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold">Requis</span>
                                  )}
                                </div>
                                {ds.originalName && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{ds.originalName}</p>
                                )}
                                {ds.status === "REJECTED" && ds.rejectionReason && (
                                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    {ds.rejectionReason}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getDocStatusBadge(ds.status)}
                              {(ds.status === "NOT_PROVIDED" || ds.status === "REJECTED") && (
                                <label className="cursor-pointer">
                                  <input
                                    ref={docTypeFileRef}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    className="hidden"
                                    onChange={(e) => handleDocTypeUpload(e, ds.documentTypeId)}
                                  />
                                  <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                                    uploadingDocType === ds.documentTypeId
                                      ? "bg-primary/20 text-primary"
                                      : "bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                                  }`}>
                                    {uploadingDocType === ds.documentTypeId ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Upload className="h-3 w-3" />
                                    )}
                                    {ds.status === "REJECTED" ? "Renvoyer" : "Envoyer"}
                                  </span>
                                </label>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : dossier?.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {dossier.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                                <File className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{doc.documentTypeName || doc.originalName || `Document`}</p>
                                <p className="text-xs text-muted-foreground">{doc.originalName}</p>
                              </div>
                            </div>
                            {getDocStatusBadge(doc.status)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border-2 border-dashed p-6 text-center">
                        <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Aucun document envoyé</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ============== TAB 3: CONTACT RH ============== */}
              <TabsContent value="contact" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      Contacter les Ressources Humaines
                    </CardTitle>
                    <CardDescription>
                      Envoyez un message à l'équipe RH pour toute question sur votre dossier
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Quick info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded-lg bg-muted/50 p-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Mail className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email RH</p>
                          <p className="text-sm font-medium">rh@takapes.info</p>
                        </div>
                      </div>
                      <div className="rounded-lg bg-muted/50 p-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Phone className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Téléphone</p>
                          <p className="text-sm font-medium">+33 1 23 45 67 89</p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Message form */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="rh-subject">Sujet</Label>
                        <Input
                          id="rh-subject"
                          placeholder="Ex: Question sur mon contrat de formation"
                          value={rhSubject}
                          onChange={(e) => setRhSubject(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rh-message">Message</Label>
                        <Textarea
                          id="rh-message"
                          placeholder="Décrivez votre demande en détail..."
                          value={rhMessage}
                          onChange={(e) => setRhMessage(e.target.value)}
                          rows={6}
                          className="resize-none"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          className="gap-2 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                          onClick={handleSendMessage}
                          disabled={sendingMessage || !rhSubject.trim() || !rhMessage.trim()}
                        >
                          {sendingMessage ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          Envoyer le message
                        </Button>
                        {(rhSubject || rhMessage) && (
                          <Button variant="ghost" size="sm" onClick={() => { setRhSubject(""); setRhMessage(""); }}>
                            Effacer
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ============== TAB 4: SIGNATURE ============== */}
              <TabsContent value="signature" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-primary" />
                      Signature & Validation
                    </CardTitle>
                    <CardDescription>
                      Vérifiez, validez et signez électroniquement votre contrat
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {contract ? (
                      <>
                        {/* Contract summary */}
                        <div className="rounded-xl border p-5 bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold flex items-center gap-2">
                              <FileSignature className="h-5 w-5 text-purple-600" />
                              Récapitulatif du contrat
                            </h4>
                            {getStatusBadge(contract.status)}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground">Template</p>
                              <p className="font-medium">{contract.template?.name || "Standard"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Version</p>
                              <p className="font-medium">Version {contract.currentVersion}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Date de création</p>
                              <p className="font-medium">{new Date(contract.createdAt).toLocaleDateString("fr-FR")}</p>
                            </div>
                          </div>
                          {contract.signedAt && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-sm text-green-700 dark:text-green-400 font-medium flex items-center gap-1.5">
                                <CheckCircle className="h-4 w-4" />
                                Signé le {new Date(contract.signedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Process steps */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Processus de signature</h4>
                          <SignatureStep
                            step={1}
                            title="Vérification et validation du contrat"
                            description="Vérifiez vos informations et validez dans l'onglet Contrat"
                            done={["VALIDATED", "SENT_FOR_SIGNATURE", "SIGNED", "COMPLETED"].includes(contract.status)}
                            active={["DRAFT", "PENDING_REVIEW", "CONTRACT_DRAFT", "CHANGE_REQUESTED"].includes(contract.status)}
                          />
                          <SignatureStep
                            step={2}
                            title="Signature électronique"
                            description="Signez votre contrat via DocuSign"
                            done={["SIGNED", "COMPLETED"].includes(contract.status)}
                            active={["VALIDATED", "SENT_FOR_SIGNATURE"].includes(contract.status)}
                          />
                        </div>

                        <Separator />

                        {/* Action buttons */}
                        <div className="flex flex-col gap-4">
                          {/* Sign button - when contract is validated */}
                          {["VALIDATED", "FINAL", "SENT_FOR_SIGNATURE"].includes(contract.status) && (
                            <div className="rounded-xl border-2 border-violet-200 dark:border-violet-800 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 p-6 text-center space-y-4">
                              <div className="flex justify-center">
                                <div className="h-16 w-16 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                  <FileSignature className="h-8 w-8 text-violet-600" />
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">Prêt à signer !</h4>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Votre contrat est validé. Cliquez ci-dessous pour le signer électroniquement via DocuSign.
                                </p>
                              </div>
                              <Button
                                className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white shadow-lg px-8"
                                onClick={() => handleSign()}
                                disabled={loadingSignUrl}
                                size="lg"
                              >
                                {loadingSignUrl ? (
                                  <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    Chargement du lien...
                                  </>
                                ) : (
                                  <>
                                    <FileSignature className="h-5 w-5" />
                                    Signer le contrat via DocuSign
                                    <ExternalLink className="h-4 w-4" />
                                  </>
                                )}</Button>
                            </div>
                          )}

                          {/* Signed confirmation */}
                          {contract.status === "SIGNED" && (
                            <div className="rounded-xl border-2 border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 text-center space-y-3">
                              <div className="flex justify-center">
                                <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                                  <CheckCircle className="h-8 w-8 text-green-600" />
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg text-green-700 dark:text-green-400">Contrat signé avec succès !</h4>
                                <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                                  Vous recevrez une copie par email. Merci !
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Not yet validated */}
                          {["DRAFT", "CONTRACT_DRAFT", "PENDING_REVIEW", "CHANGE_REQUESTED"].includes(contract.status) && (
                            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-5 space-y-3">
                              <div className="flex items-center gap-3">
                                <Clock className="h-5 w-5 text-amber-600 shrink-0" />
                                <div>
                                  <p className="font-medium text-sm text-amber-800 dark:text-amber-300">
                                    Contrat non encore validé
                                  </p>
                                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                                    Rendez-vous dans l'onglet Contrat pour vérifier vos informations et valider.
                                  </p>
                                </div>
                              </div>
                              <Button variant="outline" className="gap-2" onClick={() => setActiveTab("overview")}>
                                <Eye className="h-4 w-4" />
                                Aller à l'onglet Contrat
                              </Button>
                            </div>
                          )}

                          {/* View contract button */}
                          {!["DRAFT", "CONTRACT_DRAFT", "PENDING_REVIEW", "CHANGE_REQUESTED"].includes(contract.status) && (
                            <Button variant="outline" className="gap-2 self-start" onClick={() => setActiveTab("overview")}>
                              <Eye className="h-4 w-4" />
                              Voir le contrat
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FileSignature className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="font-semibold text-lg">Pas de contrat à signer</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Votre contrat sera disponible une fois créé par l'administration.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </AppLayout>
  );
}

// === Helper Components ===

function SignatureStep({ step, title, description, done, active }) {
  return (
    <div className={`flex items-start gap-4 rounded-lg p-4 transition-all ${
      done ? "bg-green-50/50 dark:bg-green-900/10 border border-green-200 dark:border-green-800" :
      active ? "bg-primary/5 border border-primary/20" :
      "bg-muted/30 border border-transparent"
    }`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
        done ? "bg-green-600 text-white" :
        active ? "bg-primary text-white" :
        "bg-muted text-muted-foreground"
      }`}>
        {done ? <Check className="h-4 w-4" /> : step}
      </div>
      <div>
        <p className={`font-medium text-sm ${done ? "text-green-700 dark:text-green-400" : active ? "text-foreground" : "text-muted-foreground"}`}>
          {title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}
